"use client"

import { useRouter } from "next/navigation"
import { useEffect } from "react"

import { useTspAthletes } from "@/lib/apps/tsp/hooks/use-athlete-queries"
import { useTspFavoriteAthletes } from "@/lib/apps/tsp/hooks/use-favorite-athlete-queries"
import { useTspUserProfile } from "@/lib/apps/tsp/hooks/use-user-profile-queries"
import { useCurrentUser } from "@/lib/nango.user/hooks/use-user-queries"
import { buildRedirectUrl } from "@/lib/new-url"

import LayoutClientSide from "./layout-client-side"

export default function TspLoggedInLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const router = useRouter()
  const userQuery = useCurrentUser()
  const profileQuery = useTspUserProfile(!!userQuery.data)
  const favoritesQuery = useTspFavoriteAthletes(!!userQuery.data)
  const athletesQuery = useTspAthletes(undefined, !!userQuery.data)

  const user = userQuery.data
  const noUser = !userQuery.isLoading && !user
  // Fantasy is free and standalone: a fresh signup can play without first
  // picking favorites, so skip the favorites onboarding gate on that route.
  const isFantasyRoute =
    typeof window !== "undefined" &&
    window.location.pathname.includes("/fantasy")
  const noFavorites =
    !!user &&
    !isFantasyRoute &&
    !favoritesQuery.isLoading &&
    (favoritesQuery.data?.length ?? 0) === 0

  useEffect(() => {
    if (noUser) {
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : ""
      if (pathname) {
        router.replace(buildRedirectUrl("/", pathname))
      } else {
        router.replace("/")
      }
    } else if (noFavorites) {
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : ""
      router.replace(pathname ? buildRedirectUrl("/setup", pathname) : "/setup")
    }
  }, [noUser, noFavorites, router])

  if (
    userQuery.isLoading ||
    noUser ||
    favoritesQuery.isLoading ||
    noFavorites
  ) {
    return null
  }

  const profile = profileQuery.data ?? null
  const favoriteIds = new Set(
    (favoritesQuery.data ?? []).map((f) => f.athlete_id),
  )
  const favorites = (athletesQuery.data ?? []).filter((a) =>
    favoriteIds.has(a.id),
  )

  return (
    <LayoutClientSide user={user} profile={profile} favorites={favorites}>
      {children}
    </LayoutClientSide>
  )
}
